import React, { Fragment, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertCircle, AlertTriangle, ArrowUpDown, Building2, ChevronDown, ChevronRight, Download, ExternalLink,
  IndianRupee, Landmark, RefreshCw, Search, Wallet, X,
} from "lucide-react";
import { EmptyState, KpiCard, StatusBadge } from "./common";
import { DrilldownBanner, useUrlFilters } from "./DrilldownBanner";
import { FetchButton, FetchDirtyHint, FetchForm, FetchPrompt, staffInTerritory, useFetchGate } from "./FetchBar";
import { PaginationBar, TableRowsSkeleton, usePagedList } from "./PaginationBar";
import { staffLabelText } from "./StaffAvatar";
import { CollectionSummaryCards, PaymentHistoryTable, ResponsibleInline, ResponsibleStaff } from "./PropertyCollectionSummary";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { MultiSelect } from "./ui/multi-select";
import { splitIds, joinIds, namesOf } from "./TerritoryFilter";
import { Skeleton } from "./ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { api, downloadBlob, errMsg, fmtDate, fmtINR } from "../lib/api";
import { useLiveCollections } from "../lib/liveCollections";

// URL keys are prefixed so they never collide with the Receipts tab filters on the same page
export const BP_FILTER_KEYS = ["bp_search", "bp_scope", "bp_zone_id", "bp_ward_id", "bp_collector_id", "bp_status", "bp_sort"];
const API_KEY = { bp_search: "search", bp_scope: "source_scope", bp_zone_id: "zone_id", bp_ward_id: "ward_id",
  bp_collector_id: "collector_id", bp_status: "status", bp_sort: "sort" };

const SCOPES = [["all", "All properties"], ["main", "Main register"], ["third_party", "Third Party"]];
const STATUSES = [["all", "Any status"], ["due", "Balance due"], ["pending", "Pending (nothing paid)"], ["partial", "Partially paid"],
  ["paid", "Fully paid"], ["no_demand", "No demand raised"]];
const SORTS = [["balance_desc", "Highest balance first"], ["collected_desc", "Most collected first"], ["demand_desc", "Highest demand first"],
  ["last_payment_desc", "Latest payment first"], ["pin", "PIN (A → Z)"]];
const SOURCE_LABEL = { excel_upload: "Excel Upload", manual_entry: "Manual Entry", new_survey: "Survey", reassessment: "Reassessment", third_party: "Third Party" };

const label = (list, v) => (list.find(([k]) => k === v) || [])[1];

/** Expanded row body: tiles + accountable staff + full receipt history (lazy-loaded once per row). */
function PropertyHistoryPanel({ code, row }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const load = () => {
    setError(null);
    api.get(`/t/${code}/collections/by-property/${row.id}/payments`).then((r) => setData(r.data))
      .catch((e) => setError(errMsg(e)));
  };
  const live = useLiveCollections(code);   // Phase 149
  useEffect(load, [code, row.id, live]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="px-4 py-4 bg-muted/30 border-t space-y-3" data-testid={`bp-history-panel-${row.pin || row.id}`}>
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)] gap-3">
        <CollectionSummaryCards summary={data?.summary || row} testId={`bp-panel-summary-${row.pin || row.id}`} />
        <ResponsibleStaff code={code} responsible={data?.responsible || row.responsible} testId={`bp-panel-responsible-${row.pin || row.id}`} />
      </div>
      <div className="flex items-center justify-between gap-2">
        <p className="font-medium text-sm">Collection history</p>
        <Button asChild variant="ghost" size="sm" className="h-7 text-xs" data-testid={`bp-open-property-${row.pin || row.id}`}>
          <Link to={`/${code}/admin/properties?focus=${row.id}`}>Open property record <ExternalLink className="h-3 w-3 ml-1" /></Link>
        </Button>
      </div>
      {error ? (
        <div className="flex items-center justify-between gap-2 text-sm text-destructive" data-testid={`bp-history-error-${row.pin || row.id}`}>
          <span>{error}</span><Button size="sm" variant="outline" onClick={load}>Retry</Button>
        </div>
      ) : !data ? (
        <div className="space-y-2" aria-hidden="true"><Skeleton className="h-8" /><Skeleton className="h-8" /><Skeleton className="h-8" /></div>
      ) : (
        <PaymentHistoryTable code={code} payments={data.items} testId={`bp-history-${row.pin || row.id}`}
          emptyText="No payments have been recorded against this property yet." />
      )}
    </div>
  );
}

/**
 * Phase 104 — Collections → By Property: one row per property with total demand, collected so far,
 * balance due and the responsible collector / zone supervisor. Rows expand into the receipt history.
 */
export function CollectionsByProperty({ code, opts, readOnly = false }) {
  const { filters, patch, clear, arrivedFiltered } = useUrlFilters(BP_FILTER_KEYS);
  // Phase 133: the query runs only on Fetch (a drilldown arrival counts); the Sort select applies immediately
  const gateFilters = useMemo(() => { const { bp_sort, ...rest } = filters; return rest; }, [filters]);
  const gate = useFetchGate(gateFilters, { autoFetch: arrivedFiltered });
  const [open, setOpen] = useState(null);
  const [exporting, setExporting] = useState(false);

  const params = useMemo(() => {
    const p = {};
    const src = gate.applied || {};
    BP_FILTER_KEYS.forEach((k) => { const v = k === "bp_sort" ? filters.bp_sort : src[k]; if (v) p[API_KEY[k]] = v; });
    return p;
  }, [gate.appliedKey, filters.bp_sort]); // eslint-disable-line react-hooks/exhaustive-deps

  const live = useLiveCollections(code);   // Phase 149: payments / demand changes anywhere refresh the totals
  const list = usePagedList(`/t/${code}/collections/by-property`, { params, initialLimit: 25, enabled: gate.fetched, reloadKey: `${gate.version}:${live}` });
  const summary = list.data?.summary;
  useEffect(() => { setOpen(null); }, [list.page, params]);
  const applied = gate.applied || {};
  const active = BP_FILTER_KEYS.filter((k) => k !== "bp_sort" && applied[k]);
  const clearAll = () => { clear(); gate.reset(); };

  // Phase 176: Zone / Ward are multi-select (comma-joined ids); wards follow the chosen zones
  const zoneSel = splitIds(filters.bp_zone_id), wardSel = splitIds(filters.bp_ward_id);
  const wardsForZone = zoneSel.length ? opts.wards.filter((w) => zoneSel.includes(w.zone_id)) : opts.wards;
  // Phase 133: collectors narrowed to the chosen Zone / Ward (assignment-based filter, so tax collectors only)
  const collectorsInArea = useMemo(() => (opts.collectors || []).filter((c) => staffInTerritory(c, filters.bp_zone_id, filters.bp_ward_id)),
    [opts.collectors, filters.bp_zone_id, filters.bp_ward_id]);
  const collectorListed = (id, zoneId, wardId) => !id || id === "unassigned" || (opts.collectors || []).some((c) => c.id === id && staffInTerritory(c, zoneId, wardId));
  const changeZone = (v) => {
    const bp_zone_id = joinIds(v);
    const bp_ward_id = joinIds(bp_zone_id ? wardSel.filter((w) => opts.wards.some((x) => x.id === w && v.includes(x.zone_id))) : wardSel);
    patch({ bp_zone_id, bp_ward_id, bp_collector_id: collectorListed(filters.bp_collector_id, bp_zone_id, bp_ward_id) ? filters.bp_collector_id : "" });
  };
  const changeWard = (v) => {
    const bp_ward_id = joinIds(v);
    patch({ bp_ward_id, bp_collector_id: collectorListed(filters.bp_collector_id, filters.bp_zone_id, bp_ward_id) ? filters.bp_collector_id : "" });
  };
  const territoryName = wardSel.length ? namesOf(filters.bp_ward_id, opts.wards, "ward_name", "this ward")
    : zoneSel.length ? namesOf(filters.bp_zone_id, opts.zones, "zone_name", "this zone") : "";
  const zoneName = namesOf(applied.bp_zone_id, opts.zones, "zone_name");
  const wardName = namesOf(applied.bp_ward_id, opts.wards, "ward_name");
  const collectorEntry = (opts.collectors || []).find((c) => c.id === applied.bp_collector_id);
  const collectorName = applied.bp_collector_id === "unassigned" ? "Unassigned" : collectorEntry ? staffLabelText(collectorEntry.name, collectorEntry.employee_id) : undefined;

  const exportExcel = async () => {
    setExporting(true);
    try {
      const qs = new URLSearchParams(params).toString();
      await downloadBlob(`/t/${code}/collections/by-property/export${qs ? `?${qs}` : ""}`,
        `${code}_collections_by_property_${new Date().toISOString().slice(0, 10)}.xlsx`);
      toast.success("By Property register exported");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setExporting(false);
    }
  };

  const chips = gate.fetched ? [
    { key: "bp_scope", label: "Scope", value: applied.bp_scope && applied.bp_scope !== "all" ? label(SCOPES, applied.bp_scope) : "" },
    { key: "bp_zone_id", label: "Zone", value: applied.bp_zone_id ? (zoneName || "…") : "" },
    { key: "bp_ward_id", label: "Ward", value: applied.bp_ward_id ? (wardName || "…") : "" },
    { key: "bp_collector_id", label: "Collector", value: applied.bp_collector_id ? (collectorName || "…") : "" },
    { key: "bp_status", label: "Status", value: applied.bp_status && applied.bp_status !== "all" ? label(STATUSES, applied.bp_status) : "" },
    { key: "bp_search", label: "Search", value: applied.bp_search },
  ] : [];

  return (
    <div data-testid="collections-by-property">
      <div className="flex items-center justify-end gap-2 mb-4">
        <Button variant="outline" size="sm" onClick={() => list.reload()} disabled={!gate.fetched || list.loading} data-testid="bp-refresh-button">
          <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${list.loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
        {!readOnly && <Button size="sm" onClick={exportExcel} disabled={exporting || !gate.fetched || list.total === 0} data-testid="bp-export-button">
          <Download className="h-3.5 w-3.5 mr-1.5" /> {exporting ? "Exporting…" : "Export Excel"}
        </Button>}
      </div>

      <DrilldownBanner chips={chips} onClear={clearAll} testId="bp-drilldown" />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4" data-testid="bp-summary">
        <KpiCard label="Properties" value={gate.fetched ? (summary?.properties ?? 0) : "—"} icon={Building2} testId="bp-kpi-properties"
          sub={!gate.fetched ? "click Fetch to load" : summary ? `${(summary.with_due || 0).toLocaleString("en-IN")} with balance due` : undefined} />
        <KpiCard label="Total demand" value={gate.fetched ? (summary?.total_demand ?? 0) : "—"} money={gate.fetched} icon={Landmark} testId="bp-kpi-demand" />
        <KpiCard label="Collected so far" value={gate.fetched ? (summary?.total_collected ?? 0) : "—"} money={gate.fetched} icon={IndianRupee} testId="bp-kpi-collected"
          sub={summary?.advance_credit > 0 ? `incl. ${fmtINR(summary.advance_credit)} advance credit` : undefined} />
        <KpiCard label="Balance due" value={gate.fetched ? (summary?.balance_due ?? 0) : "—"} money={gate.fetched} icon={Wallet} testId="bp-kpi-balance" />
      </div>

      {list.data?.warning && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-[hsl(var(--warning))]/30 bg-[hsl(var(--warning))]/10 px-3 py-2 text-sm" role="status" data-testid="bp-capped-warning">
          <AlertTriangle className="h-4 w-4 text-[hsl(var(--warning))] shrink-0" /> {list.data.warning}
        </div>
      )}

      <Card className="shadow-sm mb-4">
        <FetchForm onFetch={gate.fetch} testId="bp-filter-form">
        <CardContent className="p-4 flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input className="pl-9" placeholder="Search PIN, owner, address, mobile…" value={filters.bp_search}
              onChange={(e) => patch({ bp_search: e.target.value })} data-testid="bp-search-input" />
          </div>
          <Select value={filters.bp_scope || "all"} onValueChange={(v) => patch({ bp_scope: v === "all" ? "" : v })}>
            <SelectTrigger className="w-40" data-testid="bp-scope-select"><SelectValue placeholder="Scope" /></SelectTrigger>
            <SelectContent>{SCOPES.map(([k, l]) => <SelectItem key={k} value={k} data-testid={`bp-scope-option-${k}`}>{l}</SelectItem>)}</SelectContent>
          </Select>
          <div className="w-40"><MultiSelect options={opts.zones.map((z) => ({ value: z.id, label: z.zone_name }))} value={zoneSel} onChange={changeZone}
            allLabel="All zones" noun="zone" placeholder="Search zones…" testId="bp-zone-select" /></div>
          <div className="w-44"><MultiSelect options={wardsForZone.map((w) => ({ value: w.id, label: w.ward_name, group: zoneSel.length !== 1 ? opts.zones.find((z) => z.id === w.zone_id)?.zone_name : undefined }))}
            value={wardSel} onChange={changeWard} allLabel="All wards" noun="ward" placeholder="Search wards…" testId="bp-ward-select" /></div>
          <Select value={filters.bp_collector_id || "all"} onValueChange={(v) => patch({ bp_collector_id: v === "all" ? "" : v })}>
            <SelectTrigger className="w-52" data-testid="bp-collector-select" data-staff-count={collectorsInArea.length}><SelectValue placeholder="Collector" /></SelectTrigger>
            <SelectContent data-testid="bp-collector-options">
              <SelectItem value="all">All collectors{territoryName ? ` in ${territoryName}` : ""}</SelectItem>
              <SelectItem value="unassigned" data-testid="bp-collector-option-unassigned">Unassigned (supervisor)</SelectItem>
              {collectorsInArea.map((c) => <SelectItem key={c.id} value={c.id} data-testid={`bp-collector-option-${c.id}`}>{staffLabelText(c.name, c.employee_id)}{c.is_active === false ? " · inactive" : ""}</SelectItem>)}
              {collectorsInArea.length === 0 && (
                <div className="px-2 py-1.5 text-xs text-muted-foreground" data-testid="bp-collector-empty">
                  {territoryName ? `No collectors mapped to ${territoryName}` : "No collectors found"}
                </div>
              )}
            </SelectContent>
          </Select>
          <Select value={filters.bp_status || "all"} onValueChange={(v) => patch({ bp_status: v === "all" ? "" : v })}>
            <SelectTrigger className="w-44" data-testid="bp-status-select"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>{STATUSES.map(([k, l]) => <SelectItem key={k} value={k} data-testid={`bp-status-option-${k}`}>{l}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filters.bp_sort || "balance_desc"} onValueChange={(v) => patch({ bp_sort: v === "balance_desc" ? "" : v })}>
            <SelectTrigger className="w-48" data-testid="bp-sort-select">
              <ArrowUpDown className="h-3.5 w-3.5 mr-1.5 text-muted-foreground shrink-0" /><SelectValue placeholder="Sort" />
            </SelectTrigger>
            <SelectContent>{SORTS.map(([k, l]) => <SelectItem key={k} value={k} data-testid={`bp-sort-option-${k}`}>{l}</SelectItem>)}</SelectContent>
          </Select>
          <div className="flex items-center gap-2 ml-auto">
            <FetchDirtyHint dirty={gate.dirty} testId="bp-fetch-dirty" />
            <FetchButton type="submit" size="sm" loading={list.loading && gate.fetched} dirty={gate.dirty} fetched={gate.fetched} testId="bp-fetch-button" />
            {BP_FILTER_KEYS.some((k) => k !== "bp_sort" && filters[k]) && (
              <Button type="button" variant="ghost" size="sm" className="h-10 text-muted-foreground" onClick={clearAll} data-testid="bp-clear-filters">
                <X className="h-3.5 w-3.5 mr-1" /> Clear
              </Button>
            )}
          </div>
        </CardContent>
        </FetchForm>
      </Card>

      <Card className="shadow-sm">
        <CardContent className="p-0">
          {!gate.fetched ? (
            <FetchPrompt onFetch={gate.fetch} testId="bp-fetch-prompt"
              subtitle="Choose a scope, zone, ward, collector or status above, then click Fetch to load the property-wise register." />
          ) : list.error && !list.data ? (
            <div className="flex flex-col items-center justify-center gap-2 py-12 text-center" data-testid="bp-error">
              <AlertCircle className="h-5 w-5 text-destructive" />
              <p className="text-sm text-muted-foreground">Could not load the property register.</p>
              <Button size="sm" variant="outline" onClick={() => list.reload()} data-testid="bp-error-retry">
                <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Retry
              </Button>
            </div>
          ) : !list.data ? (
            <TableRowsSkeleton rows={8} cols={7} testId="bp-skeleton" />
          ) : list.items.length === 0 ? (
            <EmptyState title="No properties match" subtitle={active.length ? "Try clearing a filter or widening the scope, then Fetch again." : "Properties appear here once demand is uploaded."}
              action={active.length ? <Button variant="outline" size="sm" onClick={clearAll} data-testid="bp-empty-clear">Clear filters</Button> : null} />
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table data-testid="bp-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-8" aria-label="Expand" />
                      <TableHead>Property</TableHead>
                      <TableHead className="hidden md:table-cell">Zone / Ward</TableHead>
                      <TableHead className="text-right">Total demand</TableHead>
                      <TableHead className="text-right">Collected</TableHead>
                      <TableHead className="text-right">Balance due</TableHead>
                      <TableHead className="hidden sm:table-cell">Status</TableHead>
                      <TableHead className="hidden lg:table-cell">Responsible</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {list.items.map((r) => {
                      const isOpen = open === r.id;
                      const key = r.pin || r.id;
                      return (
                        <Fragment key={r.id}>
                          <TableRow className={`cursor-pointer hover:bg-muted/60 ${isOpen ? "bg-muted/40" : ""}`}
                            onClick={() => setOpen(isOpen ? null : r.id)} aria-expanded={isOpen} data-testid={`bp-row-${key}`}>
                            <TableCell className="pr-0 text-muted-foreground">
                              {isOpen ? <ChevronDown className="h-4 w-4" data-testid={`bp-row-collapse-${key}`} /> : <ChevronRight className="h-4 w-4" data-testid={`bp-row-expand-${key}`} />}
                            </TableCell>
                            <TableCell>
                              <span className="flex items-center gap-1.5 flex-wrap">
                                <span className="font-mono text-sm text-primary" data-testid={`bp-row-pin-${key}`}>{r.pin || "—"}</span>
                                {r.source === "third_party" && <Badge variant="outline" className="text-[10px] bg-violet-50 text-violet-800 border-violet-200">Third Party</Badge>}
                              </span>
                              <p className="text-xs text-muted-foreground truncate max-w-[220px]">{r.owner_name || "—"}</p>
                              <p className="text-[11px] text-muted-foreground md:hidden">{r.ward_label || "—"}{r.zone_label ? ` • ${r.zone_label}` : ""}</p>
                            </TableCell>
                            <TableCell className="hidden md:table-cell text-sm">
                              <p>{r.zone_label || "—"}</p>
                              <p className="text-xs text-muted-foreground">{r.ward_label || "—"}{r.source && r.source !== "third_party" ? ` • ${SOURCE_LABEL[r.source] || r.source}` : ""}</p>
                            </TableCell>
                            <TableCell className="text-right tabular-nums whitespace-nowrap" data-testid={`bp-row-demand-${key}`}>{fmtINR(r.total_demand)}</TableCell>
                            <TableCell className="text-right tabular-nums whitespace-nowrap text-[hsl(var(--success))]" data-testid={`bp-row-collected-${key}`}>
                              {fmtINR(r.total_collected)}
                              {r.advance_credit > 0.004 && <p className="text-[10px] text-muted-foreground font-normal">incl. {fmtINR(r.advance_credit)} adv.</p>}
                            </TableCell>
                            <TableCell className={`text-right tabular-nums whitespace-nowrap font-medium ${r.balance_due > 0.004 ? "text-destructive" : ""}`} data-testid={`bp-row-balance-${key}`}>
                              {fmtINR(r.balance_due)}
                              {r.last_payment_date && <p className="text-[10px] text-muted-foreground font-normal">last {fmtDate(r.last_payment_date)}</p>}
                            </TableCell>
                            <TableCell className="hidden sm:table-cell">
                              {r.has_demand ? <StatusBadge status={r.payment_status} />
                                : <Badge variant="outline" className="bg-muted text-muted-foreground font-normal" data-testid="status-badge-no-demand">No demand</Badge>}
                            </TableCell>
                            <TableCell className="hidden lg:table-cell">
                              <ResponsibleInline code={code} responsible={r.responsible} testId={`bp-row-responsible-${key}`} />
                            </TableCell>
                          </TableRow>
                          {isOpen && (
                            <TableRow className="hover:bg-transparent" data-testid={`bp-row-expanded-${key}`}>
                              <TableCell colSpan={8} className="p-0">
                                <PropertyHistoryPanel code={code} row={r} />
                              </TableCell>
                            </TableRow>
                          )}
                        </Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
              <div className="px-4 pb-3 border-t">
                <PaginationBar page={list.page} limit={list.limit} total={list.total} loading={list.loading}
                  onPageChange={list.setPage} onLimitChange={list.setLimit} testId="bp-pagination" />
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
